import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';
import * as XLSX from 'xlsx';
import {
  AjusteSocios,
  RESERVA_SALDO_INICIAL_PADRAO,
  SOCIOS_PADRAO,
  SociosMesCalc,
  SociosMesInput,
  SocioParticipacao,
  criarAnoInputs,
  criarMesInputVazio,
  recalcularAnoSocios
} from './fluxo-caixa-socios.logic';

interface FluxoCaixaLinha {
  id: number;
  codigo: string;
  nome: string;
  tipo: string;
  natureza: string;
  valores: number[];
  total: number;
  filhos: FluxoCaixaLinha[];
}

type IndicadorTipo =
  | 'receita'
  | 'receita-pizza'
  | 'despesas'
  | 'despesa-pizza'
  | 'despesas-pessoal'
  | 'despesas-admin'
  | 'despesas-marketing'
  | 'investimentos'
  | 'lucratividade'
  | 'variacao-receita'
  | 'processos';

type IndicadorVisual = 'tabela-mensal' | 'pizza' | 'percentual-mensal';

interface IndicadorBotao {
  id: IndicadorTipo;
  titulo: string;
}

interface IndicadorSerie {
  label: string;
  valores: number[];
  total?: number;
}

interface IndicadorView {
  id: IndicadorTipo;
  titulo: string;
  subtitulo: string;
  visual: IndicadorVisual;
  colunaValor: string;
  formato: 'moeda' | 'percentual' | 'inteiro';
  valoresMensais: number[];
  mediaAno: number;
  seriesPizza?: IndicadorSerie[];
  chartConfig?: any;
  chartId?: string;
}

interface IndicadorPdcaLinha {
  idFinIndicadorPdca: number;
  mesPeriodo: string;
  fato: string;
  causa: string;
  acaoMelhoria: string;
  responsavel: string;
  prazo: string;
  dataCadastro: string;
}

interface AjaxApiResponse {
  hasErrors?: boolean;
  HasErrors?: boolean;
  message?: string;
  Message?: string;
}

type LinhaExcel = Array<string | number>;

@Component({
  selector: 'app-fluxo-caixa',
  templateUrl: './fluxo-caixa.component.html',
  styleUrls: ['./fluxo-caixa.component.scss']
})
export class FluxoCaixaComponent implements OnInit, OnDestroy {
  ano = new Date().getFullYear();
  mesCompetencia = new Date().getMonth() + 1;
  meses: string[] = [];
  mesesAbrev = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  linhas: FluxoCaixaLinha[] = [];
  linhasAnoAnterior: FluxoCaixaLinha[] = [];
  totaisEntradas: number[] = [];
  totaisSaidas: number[] = [];
  totaisEntradasAnoAnterior: number[] = [];
  saldoMes: number[] = [];
  processosPorMes: number[] = [];
  /** Saldo vindo da API (movimentos antes do ano). */
  saldoAnteriorApi = 0;
  /** Saldo inicial informado (simula dezembro do ano anterior). */
  saldoInicialManual: number | null = null;
  saldoAnterior = 0;
  expandido: { [id: number]: boolean } = {};
  arrastandoTabela = false;

  modalCompetenciaAberto = false;
  indicadorAtivo: IndicadorView | null = null;
  exibirGraficoIndicador = false;
  pdcaItens: IndicadorPdcaLinha[] = [];
  carregandoPdca = false;
  salvandoPdca = false;

  /** Distribuição de lucros (regras da planilha FC). */
  socios: SocioParticipacao[] = SOCIOS_PADRAO.map((s) => ({ ...s }));
  reservaSaldoInicial = RESERVA_SALDO_INICIAL_PADRAO;
  sociosMesInputs: SociosMesInput[] = criarAnoInputs();
  sociosMesCalc: SociosMesCalc[] = [];
  mostrarDistribuicaoSocios = true;

  readonly indicadores: IndicadorBotao[] = [
    { id: 'receita', titulo: 'Receita' },
    { id: 'receita-pizza', titulo: 'Receita Pizza' },
    { id: 'despesas', titulo: 'Despesas' },
    { id: 'despesa-pizza', titulo: 'Despesas Pizza' },
    { id: 'despesas-pessoal', titulo: 'Despesas de Pessoal' },
    { id: 'despesas-admin', titulo: 'Despesas Administrativas' },
    { id: 'despesas-marketing', titulo: 'Despesas Prop e Marketing' },
    { id: 'investimentos', titulo: 'Investimentos' },
    { id: 'lucratividade', titulo: 'Lucratividade' },
    { id: 'variacao-receita', titulo: 'Variação da Receita' },
    { id: 'processos', titulo: 'Numero de Processos' }
  ];

  private posicaoInicialX = 0;
  private scrollInicialX = 0;
  private chartRenderTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.carregarSociosStorage();
    this.carregarSaldoInicialStorage();
    this.carregarRelatorio();
  }

  ngOnDestroy(): void {
    this.cancelarRenderGrafico();
  }

  get nomeMesCompetencia(): string {
    return this.meses[this.mesCompetencia - 1] || this.mesesAbrev[this.mesCompetencia - 1] || '';
  }

  carregarRelatorio(): void {
    this.util.showLoading();
    forkJoin({
      atual: this.ajaxService.get(`${API}financeiro/FluxoCaixa/Relatorio?ano=${this.ano}`),
      anterior: this.ajaxService.get(`${API}financeiro/FluxoCaixa/Relatorio?ano=${this.ano - 1}`)
    }).subscribe({
      next: ({ atual, anterior }: { atual: AjaxApiResponse; anterior: AjaxApiResponse }) => {
        this.util.closeLoading();
        if (atual?.hasErrors) {
          this.util.toasterShowError('Erro', atual?.message || 'Nao foi possivel carregar o relatorio.');
          return;
        }

        const data = this.desempacotarResponse(atual);
        this.meses = Array.isArray(data.meses) ? data.meses : [];
        this.saldoAnteriorApi = Number(data.saldoanterior || 0);
        this.totaisEntradas = this.normalizarNumeros(data.totaisentradas);
        this.totaisSaidas = this.normalizarNumeros(data.totaissaidas);
        this.processosPorMes = this.normalizarInteiros(data.processospormes);
        this.linhas = this.normalizarLinhas(data.linhas);
        this.expandido = {};
        this.marcarExpandido(this.linhas);
        this.aplicarSaldoInicial();

        if (!anterior?.hasErrors) {
          const dataAnterior = this.desempacotarResponse(anterior);
          this.totaisEntradasAnoAnterior = this.normalizarNumeros(dataAnterior.totaisentradas);
          this.linhasAnoAnterior = this.normalizarLinhas(dataAnterior.linhas);
        } else {
          this.totaisEntradasAnoAnterior = this.criarVetorZerado();
          this.linhasAnoAnterior = [];
        }

        if (this.indicadorAtivo) {
          this.reabrirIndicadorAtivo();
        }

        this.recalcularDistribuicaoSocios();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Falha ao carregar relatorio de fluxo de caixa.');
      }
    });
  }

  alterarAno(delta: number): void {
    this.persistirSociosStorage(false);
    this.persistirSaldoInicialStorage();
    this.ano += delta;
    this.carregarSociosStorage();
    this.carregarSaldoInicialStorage();
    this.carregarRelatorio();
  }

  onSaldoInicialChange(valor: number | string): void {
    if (valor === '' || valor == null) {
      this.saldoInicialManual = null;
    } else {
      this.saldoInicialManual = Number(valor);
      if (Number.isNaN(this.saldoInicialManual)) {
        this.saldoInicialManual = null;
      }
    }
    this.aplicarSaldoInicial();
    this.persistirSaldoInicialStorage();
  }

  limparSaldoInicial(): void {
    this.saldoInicialManual = null;
    this.aplicarSaldoInicial();
    this.persistirSaldoInicialStorage();
  }

  private aplicarSaldoInicial(): void {
    // Saldo inicial vem do título lançado na importação (API); input manual foi removido da tela.
    this.saldoInicialManual = null;
    const base = Number(this.saldoAnteriorApi || 0);
    this.saldoAnterior = base;
    this.saldoMes = this.calcularSaldoMesLocal(base, this.totaisEntradas, this.totaisSaidas);
  }

  private calcularSaldoMesLocal(saldoIni: number, entradas: number[], saidas: number[]): number[] {
    const n = Math.max(entradas?.length || 0, saidas?.length || 0, 12);
    const resultado = new Array(n).fill(0);
    let acumulado = Number(saldoIni || 0);
    for (let i = 0; i < n; i++) {
      acumulado += Number(entradas[i] || 0) - Number(saidas[i] || 0);
      resultado[i] = acumulado;
    }
    return resultado;
  }

  private chaveStorageSaldoInicial(): string {
    return `fluxo-caixa-saldo-inicial-${this.ano}`;
  }

  private persistirSaldoInicialStorage(): void {
    try {
      localStorage.removeItem(this.chaveStorageSaldoInicial());
    } catch { /* ignore */ }
  }

  private carregarSaldoInicialStorage(): void {
    try {
      localStorage.removeItem(this.chaveStorageSaldoInicial());
    } catch { /* ignore */ }
    this.saldoInicialManual = null;
  }

  abrirSelecaoCompetencia(): void {
    this.modalCompetenciaAberto = true;
  }

  fecharSelecaoCompetencia(): void {
    this.modalCompetenciaAberto = false;
  }

  confirmarCompetencia(): void {
    this.modalCompetenciaAberto = false;
    if (this.indicadorAtivo) {
      this.reabrirIndicadorAtivo();
    }
  }

  abrirIndicador(id: IndicadorTipo): void {
    try {
      this.cancelarRenderGrafico();
      this.exibirGraficoIndicador = false;
      this.indicadorAtivo = this.montarIndicador(id);
      this.agendarRenderGrafico();
      this.carregarPdcaIndicador();
    } catch (e) {
      console.error('Erro ao abrir indicador', e);
      this.util.toasterShowError('Erro', 'Nao foi possivel abrir o indicador.');
    }
  }

  fecharIndicador(): void {
    this.cancelarRenderGrafico();
    this.exibirGraficoIndicador = false;
    this.indicadorAtivo = null;
    this.pdcaItens = [];
  }

  obterIconeIndicador(id: IndicadorTipo): string {
    const icones: Record<IndicadorTipo, string> = {
      receita: 'fa-line-chart',
      'receita-pizza': 'fa-pie-chart',
      despesas: 'fa-bar-chart',
      'despesa-pizza': 'fa-pie-chart',
      'despesas-pessoal': 'fa-users',
      'despesas-admin': 'fa-building-o',
      'despesas-marketing': 'fa-bullhorn',
      investimentos: 'fa-bank',
      lucratividade: 'fa-percent',
      'variacao-receita': 'fa-exchange',
      processos: 'fa-folder-open-o'
    };
    return icones[id] || 'fa-bar-chart';
  }

  private reabrirIndicadorAtivo(): void {
    if (!this.indicadorAtivo) return;
    const id = this.indicadorAtivo.id;
    this.cancelarRenderGrafico();
    this.exibirGraficoIndicador = false;
    this.indicadorAtivo = this.montarIndicador(id);
    this.agendarRenderGrafico();
    this.carregarPdcaIndicador();
  }

  get opcoesMesPeriodoPdca(): string[] {
    const anoRef = this.ano;
    return this.mesesAbrev.map((mes) => `${mes}/${anoRef}`);
  }

  adicionarLinhaPdca(): void {
    const mesPadrao = this.opcoesMesPeriodoPdca[this.mesCompetencia - 1] || '';
    this.pdcaItens = [
      ...this.pdcaItens,
      {
        idFinIndicadorPdca: 0,
        mesPeriodo: mesPadrao,
        fato: '',
        causa: '',
        acaoMelhoria: '',
        responsavel: '',
        prazo: '',
        dataCadastro: '',
      },
    ];
  }

  removerLinhaPdca(index: number): void {
    const linha = this.pdcaItens[index];
    if (!linha) return;

    if (linha.idFinIndicadorPdca > 0) {
      this.util.showLoading();
      this.ajaxService
        .delete(`${API}financeiro/FluxoCaixa/Pdca/${linha.idFinIndicadorPdca}`)
        .subscribe({
          next: (response) => {
            this.util.closeLoading();
            if (response?.hasErrors) {
              this.util.toasterShowError('Erro', response?.message || 'Não foi possível excluir o registro PDCA.');
              return;
            }
            this.pdcaItens = this.pdcaItens.filter((_, i) => i !== index);
            this.util.toasterShowSuccess('Sucesso', 'Registro PDCA excluído.');
          },
          error: () => {
            this.util.closeLoading();
            this.util.toasterShowError('Erro', 'Falha ao excluir registro PDCA.');
          },
        });
      return;
    }

    this.pdcaItens = this.pdcaItens.filter((_, i) => i !== index);
  }

  salvarPdcaIndicador(): void {
    if (!this.indicadorAtivo) return;

    const linhasValidas = this.pdcaItens.filter((l) => this.toText(l.mesPeriodo));
    if (!linhasValidas.length) {
      this.util.toasterShowWarning('Atenção', 'Informe ao menos uma linha com mês/período.');
      return;
    }

    this.salvandoPdca = true;
    this.util.showLoading();

    const requests = linhasValidas.map((linha) => {
      const payload = {
        ID_FIN_INDICADOR_PDCA: Number(linha.idFinIndicadorPdca || 0),
        ANO: this.ano,
        TIPO_INDICADOR: this.indicadorAtivo!.id,
        MES_PERIODO: this.toText(linha.mesPeriodo),
        FATO: this.toText(linha.fato),
        CAUSA: this.toText(linha.causa),
        ACAO_MELHORIA: this.toText(linha.acaoMelhoria),
        RESPONSAVEL: this.toText(linha.responsavel),
        PRAZO: linha.prazo ? linha.prazo : null,
      };
      return this.ajaxService.post(`${API}financeiro/FluxoCaixa/Pdca`, payload);
    });

    forkJoin(requests).subscribe({
      next: (responses: AjaxApiResponse[]) => {
        const comErro = (responses || []).find((r) => r?.hasErrors || r?.HasErrors);
        if (comErro) {
          this.finalizarSalvarPdca(false, comErro?.message || comErro?.Message || 'Erro ao salvar PDCA.');
          return;
        }
        this.finalizarSalvarPdca(true);
      },
      error: () => this.finalizarSalvarPdca(false, 'Falha ao salvar registros PDCA.'),
    });
  }

  private finalizarSalvarPdca(sucesso: boolean, mensagem?: string): void {
    this.salvandoPdca = false;
    this.util.closeLoading();
    if (!sucesso) {
      this.util.toasterShowError('Erro', mensagem || 'Não foi possível salvar o PDCA.');
      return;
    }
    this.util.toasterShowSuccess('Sucesso', 'Acompanhamento PDCA salvo.');
    this.carregarPdcaIndicador();
  }

  private carregarPdcaIndicador(): void {
    if (!this.indicadorAtivo) {
      this.pdcaItens = [];
      this.cdr.detectChanges();
      return;
    }

    this.carregandoPdca = true;
    const tipo = encodeURIComponent(this.indicadorAtivo.id);
    const url = `${API}financeiro/FluxoCaixa/Pdca?ano=${this.ano}&tipoIndicador=${tipo}`;
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.carregandoPdca = false;
        if (response?.hasErrors || response?.HasErrors) {
          this.pdcaItens = [];
          this.cdr.detectChanges();
          return;
        }
        const lista = this.extrairListaResponse(response);
        this.pdcaItens = lista.map((item: any) => this.mapearLinhaPdca(item));
        this.cdr.detectChanges();
      },
      error: () => {
        this.carregandoPdca = false;
        this.pdcaItens = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar o acompanhamento PDCA.');
        this.cdr.detectChanges();
      },
    });
  }

  /** Desempacota `data` da API sem converter array em objeto (evita perder lista PDCA). */
  private extrairListaResponse(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 4; i++) {
      if (Array.isArray(atual)) {
        break;
      }
      if (
        atual &&
        typeof atual === 'object' &&
        Object.prototype.hasOwnProperty.call(atual, 'data') &&
        atual.data != null
      ) {
        atual = atual.data;
      } else {
        break;
      }
    }
    if (Array.isArray(atual)) {
      return atual;
    }
    if (atual && typeof atual === 'object') {
      return Object.values(atual).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }

  private mapearLinhaPdca(item: any): IndicadorPdcaLinha {
    const obj = this.util.converterParaLowercase(item || {});
    const prazoRaw = obj.prazo;
    let prazo = '';
    if (prazoRaw) {
      const d = new Date(prazoRaw);
      if (!Number.isNaN(d.getTime())) {
        prazo = d.toISOString().slice(0, 10);
      }
    }
    const dataCadastro =
      obj.datacadastro || obj.data_cadastro || obj.datacriacao || obj.data_criacao || '';
    return {
      idFinIndicadorPdca: Number(
        obj.idfinindicadorpdca ||
          obj.id_fin_indicador_pdca ||
          item?.idFinIndicadorPdca ||
          item?.ID_FIN_INDICADOR_PDCA ||
          0
      ),
      mesPeriodo: this.toText(obj.mesperiodo || obj.mes_periodo),
      fato: this.toText(obj.fato),
      causa: this.toText(obj.causa),
      acaoMelhoria: this.toText(obj.acaomelhoria || obj.acao_melhoria),
      responsavel: this.toText(obj.responsavel),
      prazo,
      dataCadastro: dataCadastro ? String(dataCadastro) : '',
    };
  }

  private toText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  private agendarRenderGrafico(): void {
    this.cancelarRenderGrafico();
    if (!this.indicadorAtivo?.chartConfig) return;

    this.chartRenderTimer = setTimeout(() => {
      this.exibirGraficoIndicador = this.podeExibirGrafico(this.indicadorAtivo);
      this.chartRenderTimer = null;
    }, 300);
  }

  private cancelarRenderGrafico(): void {
    if (this.chartRenderTimer) {
      clearTimeout(this.chartRenderTimer);
      this.chartRenderTimer = null;
    }
  }

  private podeExibirGrafico(view: IndicadorView | null): boolean {
    if (!view?.chartConfig) return false;
    if (view.visual === 'pizza') {
      return !!(view.seriesPizza && view.seriesPizza.length > 0);
    }
    return true;
  }

  toggle(item: FluxoCaixaLinha): void {
    this.expandido[item.id] = !this.isExpandido(item);
  }

  isExpandido(item: FluxoCaixaLinha): boolean {
    return this.expandido[item.id] !== false;
  }

  hasFilhos(item: FluxoCaixaLinha): boolean {
    return !!item?.filhos?.length;
  }

  isSintetica(item: FluxoCaixaLinha): boolean {
    return String(item?.tipo || '').toUpperCase() === 'SINTETICA';
  }

  formatMoney(valor: number): string {
    return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatMoneyOuTraco(valor: number): string {
    if (valor == null || Number(valor) === 0) {
      return '-';
    }
    return this.formatMoney(valor);
  }

  formatValorIndicador(valor: number | null | undefined, formato: IndicadorView['formato']): string {
    if (valor == null || Number.isNaN(valor)) return '-';
    if (formato === 'percentual') {
      const pct = Math.abs(Number(valor)) <= 1 ? Number(valor) * 100 : Number(valor);
      return this.formatPercentual(pct);
    }
    if (formato === 'inteiro') return Number(valor || 0).toLocaleString('pt-BR');
    return this.formatMoneyOuTraco(valor);
  }

  somar(valores: number[]): number {
    return (valores || []).reduce((acumulado, atual) => acumulado + Number(atual || 0), 0);
  }

  get quantidadeMeses(): number {
    return Math.max(this.meses.length, 1);
  }

  get totalEntradas(): number {
    return this.somar(this.totaisEntradas);
  }

  get totalSaidas(): number {
    return this.somar(this.totaisSaidas);
  }

  /**
   * Receitas da planilha FC: ENTRADAS − 128 (transferência) − 1112 (CDB).
   * ENTRADAS/SAÍDAS do caixa continuam com o movimento completo.
   */
  get totaisReceitas(): number[] {
    return this.calcularReceitasPlanilha(this.totaisEntradas, this.linhas);
  }

  get totalReceitas(): number {
    return this.somar(this.totaisReceitas);
  }

  get totaisReceitasAnoAnterior(): number[] {
    return this.calcularReceitasPlanilha(this.totaisEntradasAnoAnterior, this.linhasAnoAnterior);
  }

  get despesasResumo(): number[] {
    const transferencias = this.obterValoresFolhas(
      this.linhas,
      (l) => this.isSaida(l) && this.isContaCodigo(l, '264')
    );
    const aplicacaoCdb = this.obterValoresFolhas(
      this.linhas,
      (l) => this.isSaida(l) && (this.isContaCodigo(l, '286') || this.linhaContem(l, 'CDB'))
    );
    const plr = this.obterPlrMes();
    return this.totaisSaidas.map((saida, i) => {
      return Number(saida || 0)
        - Number(transferencias[i] || 0)
        - Number(aplicacaoCdb[i] || 0)
        - Number(plr[i] || 0);
    });
  }

  get totalDespesasResumo(): number {
    return this.somar(this.despesasResumo);
  }

  get resultadoMensal(): number[] {
    return this.totaisReceitas.map((receita, i) => Number(receita || 0) - Number(this.despesasResumo[i] || 0));
  }

  get totalResultadoMensal(): number {
    return this.somar(this.resultadoMensal);
  }

  get lucratividadeResumo(): number[] {
    return this.totaisReceitas.map((receita, i) => {
      const valor = Number(receita || 0);
      if (valor === 0) return 0;
      return Number(this.resultadoMensal[i] || 0) / valor;
    });
  }

  get totalReceitaAnoAnterior(): number {
    return this.somar(this.totaisReceitasAnoAnterior);
  }

  get saldoFinalTotal(): number {
    if (!this.saldoMes.length) return this.saldoAnterior;
    return Number(this.saldoMes[this.saldoMes.length - 1] || 0);
  }

  get mediaLucratividadeResumo(): number | null {
    const valores = this.lucratividadeResumo
      .map((valor, i) => (Number(this.totaisReceitas[i] || 0) > 0 ? Number(valor || 0) : null))
      .filter((valor): valor is number => valor != null);
    if (!valores.length) return null;
    return valores.reduce((acc, valor) => acc + valor, 0) / valores.length;
  }

  obterSaldoAnteriorMes(indice: number): number {
    if (indice <= 0) return this.saldoAnterior;
    return Number(this.saldoMes[indice - 1] || 0);
  }

  formatLucratividadeResumo(valor: number | null | undefined): string {
    if (valor == null || Number.isNaN(valor)) return '-';
    return this.formatPercentual(Number(valor) * 100);
  }

  get mesesCabecalhoSocios(): string[] {
    if (this.meses?.length) return this.meses;
    return this.mesesAbrev.map((m) => `${m}/${String(this.ano).slice(-2)}`);
  }

  get percentualSociosDisplay(): number[] {
    return this.socios.map((s) => Number(s.percentual || 0) * 100);
  }

  onPercentualSocioChange(index: number, valor: number | string): void {
    const pct = Number(valor || 0);
    if (!this.socios[index]) return;
    this.socios[index].percentual = pct / 100;
    this.onSociosInputChange();
  }

  onSociosInputChange(): void {
    this.recalcularDistribuicaoSocios();
    this.persistirSociosStorage(false);
  }

  salvarSociosStorage(): void {
    this.persistirSociosStorage(true);
  }

  private persistirSociosStorage(mostrarToast: boolean): void {
    try {
      const payload = {
        socios: this.socios,
        reservaSaldoInicial: this.reservaSaldoInicial,
        meses: this.sociosMesInputs
      };
      localStorage.setItem(this.chaveStorageSocios(), JSON.stringify(payload));
      if (mostrarToast) {
        this.util.toasterShowSuccess('Sucesso', 'Dados dos sócios salvos neste navegador.');
      }
    } catch {
      if (mostrarToast) {
        this.util.toasterShowError('Erro', 'Não foi possível salvar localmente.');
      }
    }
  }

  limparOverrideRetiradaReserva(mesIdx: number): void {
    if (!this.sociosMesInputs[mesIdx]) return;
    this.sociosMesInputs[mesIdx].retiradaReservaManual = null;
    this.onSociosInputChange();
  }

  limparOverrideParticipacao(mesIdx: number): void {
    if (!this.sociosMesInputs[mesIdx]) return;
    this.sociosMesInputs[mesIdx].participacaoManual = null;
    this.onSociosInputChange();
  }

  limparOverrideRetirada(mesIdx: number, socioIdx: number): void {
    const input = this.sociosMesInputs[mesIdx];
    if (!input) return;
    input.retiradasManual[socioIdx] = null;
    this.onSociosInputChange();
  }

  setRetiradaReservaManual(mesIdx: number, valor: number | string): void {
    const input = this.garantirMesInput(mesIdx);
    const num = valor === '' || valor == null ? null : Number(valor);
    input.retiradaReservaManual = num != null && !Number.isNaN(num) ? num : null;
    this.onSociosInputChange();
  }

  setParticipacaoManual(mesIdx: number, valor: number | string): void {
    const input = this.garantirMesInput(mesIdx);
    const num = valor === '' || valor == null ? null : Number(valor);
    input.participacaoManual = num != null && !Number.isNaN(num) ? num : null;
    this.onSociosInputChange();
  }

  setRetiradaManual(mesIdx: number, socioIdx: number, valor: number | string): void {
    const input = this.garantirMesInput(mesIdx);
    const num = valor === '' || valor == null ? null : Number(valor);
    input.retiradasManual[socioIdx] = num != null && !Number.isNaN(num) ? num : null;
    this.onSociosInputChange();
  }

  setAluguelValor(mesIdx: number, valor: number | string): void {
    const input = this.garantirMesInput(mesIdx);
    input.aluguelValor = Number(valor || 0);
    this.onSociosInputChange();
  }

  setAjusteValor(mesIdx: number, ajusteIdx: number, valor: number | string): void {
    const input = this.garantirMesInput(mesIdx);
    while (input.ajustes.length <= ajusteIdx) {
      input.ajustes.push({ label: `teste ${input.ajustes.length + 1}`, valor: 0 });
    }
    input.ajustes[ajusteIdx].valor = Number(valor || 0);
    this.onSociosInputChange();
  }

  setAjusteLabel(ajusteIdx: number, label: string): void {
    this.sociosMesInputs.forEach((input) => {
      while (input.ajustes.length <= ajusteIdx) {
        input.ajustes.push({ label: `teste ${input.ajustes.length + 1}`, valor: 0 });
      }
      input.ajustes[ajusteIdx].label = label;
    });
    this.onSociosInputChange();
  }

  labelAjuste(ajusteIdx: number): string {
    return this.sociosMesInputs[0]?.ajustes?.[ajusteIdx]?.label || `teste ${ajusteIdx + 1}`;
  }

  calcMes(mesIdx: number): SociosMesCalc | null {
    return this.sociosMesCalc[mesIdx] || null;
  }

  formatMoneyNullable(valor: number | null | undefined): string {
    if (valor == null || Number.isNaN(Number(valor))) return '';
    return this.formatMoney(Number(valor));
  }

  trackByIndex(index: number): number {
    return index;
  }

  private garantirMesInput(mesIdx: number): SociosMesInput {
    while (this.sociosMesInputs.length <= mesIdx) {
      this.sociosMesInputs.push(criarMesInputVazio());
    }
    if (!this.sociosMesInputs[mesIdx]) {
      this.sociosMesInputs[mesIdx] = criarMesInputVazio();
    }
    return this.sociosMesInputs[mesIdx];
  }

  recalcularDistribuicaoSocios(): void {
    const resultados = this.resultadoMensal?.length
      ? [...this.resultadoMensal]
      : this.criarVetorZerado();
    while (resultados.length < 12) resultados.push(0);

    const salarios = this.obterSalariosPorMes();
    const plrPorMes = this.obterPlrPorSocioPorMes();

    this.sociosMesCalc = recalcularAnoSocios(
      this.socios,
      this.reservaSaldoInicial,
      this.sociosMesInputs,
      resultados,
      salarios,
      plrPorMes
    );
  }

  private obterSalariosPorMes(): number[] {
    const linha =
      this.encontrarLinha(
        this.linhas,
        (l) => this.isSaida(l) && this.linhaContem(l, 'SALARIO') && !this.hasFilhos(l)
      ) ||
      this.encontrarLinha(
        this.linhas,
        (l) => this.isSaida(l) && this.linhaContem(l, 'SALARIOS') && !this.hasFilhos(l)
      ) ||
      this.encontrarLinha(this.linhas, (l) => this.isSaida(l) && this.linhaContem(l, 'SALARIO'));

    return linha?.valores?.length ? this.completar12(linha.valores) : this.criarVetorZerado();
  }

  /** Tenta localizar PLR por sócio (ex.: "Socio 1 PLR"); se não achar, fica 0 (editável). */
  private obterPlrPorSocioPorMes(): number[][] {
    const porMes: number[][] = Array.from({ length: 12 }, () => [0, 0, 0, 0]);
    this.coletarPlrPorSocio(this.linhas, porMes);
    return porMes;
  }

  private coletarPlrPorSocio(nos: FluxoCaixaLinha[], porMes: number[][]): void {
    (nos || []).forEach((item) => {
      if (this.isSaida(item) && !this.hasFilhos(item)) {
        const nome = this.normalizarTexto(item.nome || '');
        if (nome.includes('PLR')) {
          for (let s = 1; s <= 4; s++) {
            if (nome.includes(`SOCIO ${s}`) || nome.includes(`SOCIO${s}`)) {
              (item.valores || []).forEach((valor, i) => {
                if (i < 12) {
                  porMes[i][s - 1] = Number(porMes[i][s - 1] || 0) + Number(valor || 0);
                }
              });
            }
          }
        }
      }
      if (item.filhos?.length) {
        this.coletarPlrPorSocio(item.filhos, porMes);
      }
    });
  }

  private completar12(valores: number[]): number[] {
    const out = this.criarVetorZerado();
    (valores || []).forEach((v, i) => {
      if (i < 12) out[i] = Number(v || 0);
    });
    return out;
  }

  private chaveStorageSocios(): string {
    return `fluxo-caixa-socios-${this.ano}`;
  }

  private carregarSociosStorage(): void {
    try {
      const raw = localStorage.getItem(this.chaveStorageSocios());
      if (!raw) {
        this.socios = SOCIOS_PADRAO.map((s) => ({ ...s }));
        this.reservaSaldoInicial = RESERVA_SALDO_INICIAL_PADRAO;
        this.sociosMesInputs = criarAnoInputs();
        return;
      }
      const data = JSON.parse(raw);
      if (Array.isArray(data?.socios) && data.socios.length) {
        this.socios = data.socios.map((s: SocioParticipacao, i: number) => ({
          id: Number(s.id || i + 1),
          nome: String(s.nome || `Socio ${i + 1}`),
          percentual: Number(s.percentual || 0),
          saldoInicial: Number(s.saldoInicial || 0)
        }));
      }
      if (data?.reservaSaldoInicial != null) {
        this.reservaSaldoInicial = Number(data.reservaSaldoInicial);
      }
      if (Array.isArray(data?.meses) && data.meses.length) {
        this.sociosMesInputs = data.meses.map((m: SociosMesInput) => this.normalizarMesInput(m));
        while (this.sociosMesInputs.length < 12) {
          this.sociosMesInputs.push(criarMesInputVazio());
        }
      }
    } catch {
      this.socios = SOCIOS_PADRAO.map((s) => ({ ...s }));
      this.reservaSaldoInicial = RESERVA_SALDO_INICIAL_PADRAO;
      this.sociosMesInputs = criarAnoInputs();
    }
  }

  private normalizarMesInput(m: SociosMesInput): SociosMesInput {
    const base = criarMesInputVazio();
    if (!m) return base;
    base.retiradaReservaManual =
      m.retiradaReservaManual == null ? null : Number(m.retiradaReservaManual);
    base.participacaoManual = m.participacaoManual == null ? null : Number(m.participacaoManual);
    base.aluguelValor = Number(m.aluguelValor || 0);
    base.retiradasManual = [0, 1, 2, 3].map((i) =>
      m.retiradasManual?.[i] == null ? null : Number(m.retiradasManual[i])
    );
    base.ajustes = (m.ajustes || base.ajustes).map((a: AjusteSocios, i: number) => ({
      label: String(a?.label || `teste ${i + 1}`),
      valor: Number(a?.valor || 0)
    }));
    while (base.ajustes.length < 4) {
      base.ajustes.push({ label: `teste ${base.ajustes.length + 1}`, valor: 0 });
    }
    return base;
  }

  private obterInvestimentosMes(): number[] {
    const linha = this.encontrarLinha(
      this.linhas,
      (l) => this.isSaida(l) && this.linhaContem(l, 'INVESTIMENTO')
    );
    return linha?.valores?.length ? [...linha.valores] : this.criarVetorZerado();
  }

  /** Receitas gerenciais da planilha: entradas − transferência 128 − CDB 1112. */
  private calcularReceitasPlanilha(entradas: number[], nos: FluxoCaixaLinha[]): number[] {
    const transferencias = this.obterValoresFolhas(
      nos,
      (l) => this.isEntrada(l) && this.isContaCodigo(l, '128')
    );
    const cdb = this.obterValoresFolhas(
      nos,
      (l) => this.isEntrada(l) && (this.isContaCodigo(l, '1112') || this.linhaContem(l, 'CDB'))
    );
    const base = entradas?.length ? entradas : this.criarVetorZerado();
    return base.map((valor, i) => Number(valor || 0) - Number(transferencias[i] || 0) - Number(cdb[i] || 0));
  }

  private obterValoresFolhas(
    nos: FluxoCaixaLinha[],
    filtro: (linha: FluxoCaixaLinha) => boolean
  ): number[] {
    const destino = this.criarVetorZerado();
    const walk = (items: FluxoCaixaLinha[]) => {
      (items || []).forEach((item) => {
        if (filtro(item) && !this.hasFilhos(item)) {
          (item.valores || []).forEach((valor, i) => {
            destino[i] = Number(destino[i] || 0) + Number(valor || 0);
          });
        }
        if (item.filhos?.length) {
          walk(item.filhos);
        }
      });
    };
    walk(nos);
    return destino;
  }

  private isContaCodigo(linha: FluxoCaixaLinha, codigo: string): boolean {
    return String(linha?.codigo || '').replace(/\s+/g, '') === codigo;
  }

  private obterPlrMes(): number[] {
    const destino = this.criarVetorZerado();
    this.coletarValoresPlr(this.linhas, destino);
    return destino;
  }

  private coletarValoresPlr(nos: FluxoCaixaLinha[], destino: number[]): void {
    (nos || []).forEach((item) => {
      if (this.isSaida(item)) {
        const nome = this.normalizarTexto(item.nome || '');
        if (nome.includes('PLR') && !this.hasFilhos(item)) {
          item.valores.forEach((valor, i) => {
            destino[i] = Number(destino[i] || 0) + Number(valor || 0);
          });
        }
      }
      if (item.filhos?.length) {
        this.coletarValoresPlr(item.filhos, destino);
      }
    });
  }

  calcularMedia(total: number, valoresMensais?: number[]): number {
    const totalNum = Number(total || 0);
    if (valoresMensais?.length) {
      const mesesComValor = valoresMensais.filter((v) => Number(v) !== 0).length;
      if (mesesComValor > 0) {
        return totalNum / mesesComValor;
      }
    }
    return totalNum / this.quantidadeMeses;
  }

  calcularMediaLinha(item: FluxoCaixaLinha): number {
    return this.calcularMedia(item.total, item.valores);
  }

  calcularPercentual(valor: number, base: number): number | null {
    const baseNum = Number(base || 0);
    if (baseNum === 0) return null;
    return (Number(valor || 0) / baseNum) * 100;
  }

  calcularPercentualLinha(item: FluxoCaixaLinha, basePercentual: number): number | null {
    return this.calcularPercentual(item.total, basePercentual);
  }

  formatPercentual(percentual: number | null | undefined): string {
    if (percentual == null || Number.isNaN(percentual)) {
      return '-';
    }
    return (
      Number(percentual).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + '%'
    );
  }

  iniciarArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (event.button !== 0) return;
    const alvo = event.target as HTMLElement;
    if (alvo?.closest('button, a, input, select, textarea, label')) return;
    this.arrastandoTabela = true;
    this.posicaoInicialX = event.clientX;
    this.scrollInicialX = container.scrollLeft;
  }

  moverArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (!this.arrastandoTabela) return;
    const deslocamento = event.clientX - this.posicaoInicialX;
    container.scrollLeft = this.scrollInicialX - deslocamento;
    event.preventDefault();
  }

  encerrarArrasteTabela(): void {
    this.arrastandoTabela = false;
  }

  exportarExcel(): void {
    const cabecalho: LinhaExcel = ['Plano de Contas', ...this.meses, 'Total', 'Média', '%'];
    const linhas: LinhaExcel[] = [cabecalho];

    const saldoAnteriorLinha: LinhaExcel = [
      'SALDO ANTERIOR',
      ...this.meses.map((_, i) => Number(this.obterSaldoAnteriorMes(i) || 0)),
      Number(this.saldoAnterior || 0),
      this.calcularMedia(this.saldoAnterior, this.saldoMes),
      ''
    ];
    linhas.push(saldoAnteriorLinha);

    linhas.push([
      'ENTRADAS',
      ...this.totaisEntradas.map((v) => Number(v || 0)),
      this.totalEntradas,
      this.calcularMedia(this.totalEntradas, this.totaisEntradas),
      this.formatPercentual(this.totalEntradas > 0 ? 100 : null)
    ]);
    linhas.push(...this.montarLinhasExcel(this.linhas, 'ENTRADA', 0, this.totalEntradas));

    linhas.push([
      'SAIDAS',
      ...this.totaisSaidas.map((v) => Number(v || 0)),
      this.totalSaidas,
      this.calcularMedia(this.totalSaidas, this.totaisSaidas),
      this.formatPercentual(this.totalSaidas > 0 ? 100 : null)
    ]);
    linhas.push(...this.montarLinhasExcel(this.linhas, 'SAIDA', 0, this.totalSaidas));
    linhas.push(...this.montarLinhasTotalizadoresExcel());

    const worksheet = XLSX.utils.aoa_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'FluxoCaixa');
    XLSX.writeFile(workbook, `fluxo-caixa-${this.ano}.xlsx`);
  }

  private montarLinhasTotalizadoresExcel(): LinhaExcel[] {
    const lucratividadeMedia = this.mediaLucratividadeResumo ?? 0;

    return [
      [
        'SALDO FINAL',
        ...this.saldoMes.map((v) => Number(v || 0)),
        this.saldoFinalTotal,
        this.calcularMedia(this.saldoFinalTotal, this.saldoMes),
        ''
      ],
      [
        'Receitas',
        ...this.totaisReceitas.map((v) => Number(v || 0)),
        this.totalReceitas,
        this.calcularMedia(this.totalReceitas, this.totaisReceitas),
        ''
      ],
      [
        'Despesas',
        ...this.despesasResumo.map((v) => Number(v || 0)),
        this.totalDespesasResumo,
        this.calcularMedia(this.totalDespesasResumo, this.despesasResumo),
        ''
      ],
      [
        'Resultado Mensal',
        ...this.resultadoMensal.map((v) => Number(v || 0)),
        this.totalResultadoMensal,
        this.calcularMedia(this.totalResultadoMensal, this.resultadoMensal),
        ''
      ],
      [
        'Lucratividade Financeira',
        ...this.lucratividadeResumo.map((v) => Number(v || 0)),
        this.totalReceitas > 0 ? this.totalResultadoMensal / this.totalReceitas : 0,
        lucratividadeMedia,
        ''
      ],
      [
        `Receita ${this.ano - 1}`,
        ...this.totaisReceitasAnoAnterior.map((v) => Number(v || 0)),
        this.totalReceitaAnoAnterior,
        this.calcularMedia(this.totalReceitaAnoAnterior, this.totaisReceitasAnoAnterior),
        ''
      ]
    ];
  }

  private montarIndicador(id: IndicadorTipo): IndicadorView {
    const mesIdx = this.mesCompetencia - 1;
    const nomeMes = this.nomeMesCompetencia;

    switch (id) {
      case 'receita':
        return this.criarViewMensal(
          id,
          'Receitas',
          `Receitas por mês — ${this.ano}`,
          this.totaisReceitas,
          'moeda',
          'Receita',
          'bar'
        );
      case 'receita-pizza': {
        const receitas = this.obterFilhosReceitaPizza();
        const series = receitas.map((item) => ({
          label: item.nome,
          valores: item.valores,
          total: Number(item.valores[mesIdx] || 0)
        }));
        return this.criarViewPizza(
          id,
          'Tipos de Receitas',
          `Distribuição em ${nomeMes}/${this.ano}`,
          series,
          'Receita'
        );
      }
      case 'despesas':
        return this.criarViewMensal(
          id,
          'Despesas',
          `Despesas por mês — ${this.ano}`,
          this.despesasResumo,
          'moeda',
          'Despesa',
          'bar'
        );
      case 'despesa-pizza': {
        const grupos = this.obterGruposDespesaPizza();
        const series = grupos.map((item) => ({
          label: item.nome,
          valores: item.valores,
          total: Number(item.valores[mesIdx] || 0)
        }));
        return this.criarViewPizza(
          id,
          'Tipos de Despesas',
          `Distribuição em ${nomeMes}/${this.ano}`,
          series,
          'Despesas'
        );
      }
      case 'despesas-pessoal':
        return this.criarViewConta(
          id,
          'Despesas de Pessoal',
          ['RECURSOS HUMANOS', 'PESSOAL'],
          'Desp Pessoal'
        );
      case 'despesas-admin':
        return this.criarViewConta(
          id,
          'Despesas Administrativas',
          ['DESPESAS ADMINISTRATIVAS', 'ADMINISTRATIV'],
          'Despesa'
        );
      case 'despesas-marketing':
        return this.criarViewConta(
          id,
          'Despesas Propaganda e Marketing',
          ['PROPAGANDA', 'MARKETING'],
          'Despesa'
        );
      case 'investimentos':
        return this.criarViewConta(
          id,
          'Investimentos Realizados',
          ['INVESTIMENTOS'],
          'Investimento'
        );
      case 'lucratividade': {
        const valores = this.calcularLucratividadeMensal();
        return this.criarViewMensal(
          id,
          'Lucratividade Financeira',
          `Margem (Receitas − Despesas) / Receitas — ${this.ano}`,
          valores,
          'percentual',
          'Lucratividade',
          'line'
        );
      }
      case 'variacao-receita': {
        const valores = this.calcularVariacaoReceitaMensal();
        return this.criarViewMensal(
          id,
          'Variação da Receita',
          `% de variação mensal da receita — ${this.ano}`,
          valores,
          'percentual',
          'Variação',
          'line'
        );
      }
      case 'processos':
        return this.criarViewMensal(
          id,
          'Nº de processos (contas recebidas)',
          `Quantidade de lançamentos em contas a receber por mês — ${this.ano}`,
          this.processosPorMes.map((x) => Number(x || 0)),
          'inteiro',
          'Processos',
          'bar'
        );
      default:
        return this.criarViewMensal(id, 'Indicador', '', [], 'moeda', 'Valor', 'bar');
    }
  }

  private criarViewMensal(
    id: IndicadorTipo,
    titulo: string,
    subtitulo: string,
    valoresMensais: number[],
    formato: IndicadorView['formato'],
    colunaValor: string,
    chartType: 'bar' | 'line'
  ): IndicadorView {
    const mediaAno = this.calcularMedia(this.somar(valoresMensais), valoresMensais);
    const chartId = this.gerarChartId(id);
    return {
      id,
      titulo,
      subtitulo,
      visual: formato === 'percentual' ? 'percentual-mensal' : 'tabela-mensal',
      colunaValor,
      formato,
      valoresMensais: [...valoresMensais],
      mediaAno,
      chartId,
      chartConfig: this.montarChartMensal(chartId, titulo, valoresMensais, chartType, formato)
    };
  }

  private criarViewConta(
    id: IndicadorTipo,
    titulo: string,
    termos: string[],
    colunaValor: string
  ): IndicadorView {
    const linha =
      this.encontrarLinha(this.linhas, (l) => this.linhaContem(l, ...termos) && this.isSaida(l)) ||
      this.encontrarLinha(this.linhas, (l) => this.linhaContem(l, ...termos));
    const valores = linha?.valores?.length ? [...linha.valores] : this.criarVetorZerado();
    return this.criarViewMensal(
      id,
      titulo,
      linha ? `${linha.codigo} ${linha.nome} — ${this.ano}` : `Conta não encontrada no plano — ${this.ano}`,
      valores,
      'moeda',
      colunaValor,
      'bar'
    );
  }

  private criarViewPizza(
    id: IndicadorTipo,
    titulo: string,
    subtitulo: string,
    series: IndicadorSerie[],
    colunaValor: string
  ): IndicadorView {
    const filtradas = series.filter((s) => Number(s.total || 0) !== 0);
    const chartId = this.gerarChartId(id);
    return {
      id,
      titulo,
      subtitulo,
      visual: 'pizza',
      colunaValor,
      formato: 'moeda',
      valoresMensais: [],
      mediaAno: 0,
      seriesPizza: filtradas,
      chartId,
      chartConfig: this.montarChartPizza(chartId, filtradas)
    };
  }

  private gerarChartId(id: IndicadorTipo): string {
    return `fluxoIndicador${id.replace(/[^a-z0-9]/gi, '')}${Date.now()}`;
  }

  private sanitizarNumero(valor: any): number {
    const n = Number(valor);
    return Number.isFinite(n) ? n : 0;
  }

  private formatarRotuloGrafico(valor: number, formato: IndicadorView['formato']): string {
    const num = Number(valor || 0);
    if (!num) return '';
    if (formato === 'percentual') return this.formatPercentual(num);
    if (formato === 'inteiro') return num.toLocaleString('pt-BR');
    return this.formatMoney(num);
  }

  private obterRotulosGrafico(
    formato: IndicadorView['formato'],
    tipo: 'bar' | 'line' | 'pie',
    formatter?: (val: number, opts?: any) => string
  ): any {
    const rotulo: any = {
      enabled: true,
      dropShadow: { enabled: false },
      offsetY: tipo === 'bar' ? -6 : tipo === 'line' ? -10 : 0,
      style: {
        fontSize: '12px',
        fontWeight: 700,
        colors: ['#1f3f67']
      },
      background: {
        enabled: true,
        foreColor: '#ffffff',
        padding: 6,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: '#d1d5db',
        opacity: 1
      }
    };

    if (formatter) {
      rotulo.formatter = formatter;
    } else {
      rotulo.formatter = (val: number) => this.formatarRotuloGrafico(val, formato);
    }

    return rotulo;
  }

  private montarChartMensal(
    _chartId: string,
    titulo: string,
    valores: number[],
    tipo: 'bar' | 'line',
    formato: IndicadorView['formato']
  ): any {
    const base = (valores || []).length ? valores : this.criarVetorZerado();
    const dados = base.map((v) => {
      const bruto = formato === 'percentual' ? this.sanitizarNumero(v) * 100 : this.sanitizarNumero(v);
      return Number.isFinite(bruto) ? bruto : 0;
    });

    return {
      chart: { type: tipo, height: 300, width: '100%', toolbar: { show: false }, animations: { enabled: true } },
      series: [{ name: titulo, data: dados }],
      colors: ['#1f3f67'],
      dataLabels: this.obterRotulosGrafico(formato, tipo),
      stroke: { curve: 'smooth', width: tipo === 'line' ? 3 : 0 },
      grid: {
        padding: {
          top: tipo === 'bar' ? 28 : 12,
          left: 8,
          right: 8
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '55%',
          dataLabels: { position: 'top' }
        }
      },
      xaxis: { categories: this.mesesAbrev },
      yaxis: {
        labels: {
          formatter: (val: number) => {
            if (formato === 'percentual') return `${Number(val).toFixed(0)}%`;
            if (formato === 'inteiro') return Number(val).toFixed(0);
            return Number(val).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
          }
        }
      },
      tooltip: {
        y: {
          formatter: (val: number) => {
            if (formato === 'percentual') return this.formatPercentual(val);
            if (formato === 'inteiro') return Number(val).toLocaleString('pt-BR');
            return this.formatMoney(val);
          }
        }
      }
    };
  }

  private montarChartPizza(_chartId: string, series: IndicadorSerie[]): any {
    const labels = series.map((s) => s.label);
    const valores = series.map((s) => this.sanitizarNumero(s.total));
    return {
      chart: { type: 'pie', height: 320, width: '100%', toolbar: { show: false } },
      labels,
      series: valores,
      legend: { position: 'bottom', fontSize: '12px' },
      dataLabels: this.obterRotulosGrafico('moeda', 'pie', (_val: number, opts: any) => {
        const total = valores.reduce((a, b) => a + b, 0);
        if (!total) return '';
        const valor = valores[opts.seriesIndex];
        const pct = (valor / total) * 100;
        return `${this.formatMoney(valor)} (${pct.toFixed(1)}%)`;
      }),
      plotOptions: {
        pie: {
          dataLabels: {
            minAngleToShowLabel: 8
          }
        }
      },
      tooltip: {
        y: { formatter: (val: number) => this.formatMoney(val) }
      },
      colors: ['#1f3f67', '#c0392b', '#27ae60', '#e67e22', '#8e44ad', '#16a085', '#d35400', '#2c3e50', '#7f8c8d']
    };
  }

  private calcularLucratividadeMensal(): number[] {
    return this.totaisReceitas.map((receita, i) => {
      const valor = Number(receita || 0);
      if (valor === 0) return 0;
      return (valor - Number(this.despesasResumo[i] || 0)) / valor;
    });
  }

  private calcularVariacaoReceitaMensal(): number[] {
    return this.totaisReceitas.map((receita, i) => {
      const atual = Number(receita || 0);
      if (i === 0) return 0;
      const anterior = Number(this.totaisReceitas[i - 1] || 0);
      if (anterior === 0) return 0;
      return (atual - anterior) / anterior;
    });
  }

  private obterFilhosReceitaPizza(): FluxoCaixaLinha[] {
    const receitas = this.encontrarLinha(
      this.linhas,
      (l) => this.isEntrada(l) && this.linhaContem(l, 'RECEITA')
    );
    const excluirCdb = (l: FluxoCaixaLinha) =>
      this.isContaCodigo(l, '1112') || this.linhaContem(l, 'CDB');
    if (!receitas?.filhos?.length) {
      return this.coletarFolhasEntrada(this.linhas).filter((l) => this.somar(l.valores) > 0 && !excluirCdb(l));
    }
    const filhos = receitas.filhos.filter((f) => this.somar(f.valores) > 0 && !excluirCdb(f));
    if (filhos.length) return filhos;
    return this.coletarFolhasEntrada([receitas]).filter((l) => this.somar(l.valores) > 0 && !excluirCdb(l));
  }

  private obterGruposDespesaPizza(): FluxoCaixaLinha[] {
    const termosGrupo = [
      'CUSTOS',
      'VARIAV',
      'RECURSOS HUMANOS',
      'ADMINISTRATIV',
      'PROPAGANDA',
      'MARKETING',
      'BANCAR',
      'FINANCEIR',
      'INVESTIMENTO',
      'PROLABORE'
    ];
    const grupos: FluxoCaixaLinha[] = [];
    const visitados = new Set<number>();

    const percorrer = (nos: FluxoCaixaLinha[]) => {
      (nos || []).forEach((no) => {
        if (this.isSaida(no) && termosGrupo.some((t) => this.linhaContem(no, t)) && !visitados.has(no.id)) {
          visitados.add(no.id);
          grupos.push(no);
        }
        if (no.filhos?.length) percorrer(no.filhos);
      });
    };

    percorrer(this.linhas);
    if (grupos.length) return grupos;

    return this.coletarFolhasSaida(this.linhas)
      .filter((l) => this.somar(l.valores) > 0)
      .slice(0, 8);
  }

  private coletarFolhasEntrada(nos: FluxoCaixaLinha[]): FluxoCaixaLinha[] {
    const folhas: FluxoCaixaLinha[] = [];
    const walk = (items: FluxoCaixaLinha[]) => {
      (items || []).forEach((item) => {
        if (!this.isEntrada(item)) return;
        if (!item.filhos?.length) folhas.push(item);
        else walk(item.filhos);
      });
    };
    walk(nos);
    return folhas;
  }

  private coletarFolhasSaida(nos: FluxoCaixaLinha[]): FluxoCaixaLinha[] {
    const folhas: FluxoCaixaLinha[] = [];
    const walk = (items: FluxoCaixaLinha[]) => {
      (items || []).forEach((item) => {
        if (!this.isSaida(item)) return;
        if (!item.filhos?.length) folhas.push(item);
        else walk(item.filhos);
      });
    };
    walk(nos);
    return folhas;
  }

  private encontrarLinha(
    nos: FluxoCaixaLinha[],
    filtro: (linha: FluxoCaixaLinha) => boolean
  ): FluxoCaixaLinha | null {
    for (const no of nos || []) {
      if (filtro(no)) return no;
      const filho = this.encontrarLinha(no.filhos, filtro);
      if (filho) return filho;
    }
    return null;
  }

  private linhaContem(linha: FluxoCaixaLinha, ...termos: string[]): boolean {
    const nome = this.normalizarTexto(linha?.nome || '');
    return termos.some((termo) => nome.includes(this.normalizarTexto(termo)));
  }

  private normalizarTexto(texto: string): string {
    return (texto || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim();
  }

  private isEntrada(linha: FluxoCaixaLinha): boolean {
    return String(linha?.natureza || '').toUpperCase() === 'ENTRADA';
  }

  private isSaida(linha: FluxoCaixaLinha): boolean {
    return String(linha?.natureza || '').toUpperCase() === 'SAIDA';
  }

  private criarVetorZerado(): number[] {
    return new Array(12).fill(0);
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private normalizarNumeros(lista: any): number[] {
    if (!Array.isArray(lista)) return [];
    return lista.map((x: any) => Number(x || 0));
  }

  private normalizarInteiros(lista: any): number[] {
    if (!Array.isArray(lista)) return new Array(12).fill(0);
    return lista.map((x: any) => Number(x || 0));
  }

  private normalizarLinhas(lista: any): FluxoCaixaLinha[] {
    if (!Array.isArray(lista)) return [];
    return lista
      .map((item: any) => {
        const obj = this.util.converterParaLowercase(item || {});
        return {
          id: Number(obj.id || 0),
          codigo: String(obj.codigo || ''),
          nome: String(obj.nome || ''),
          tipo: String(obj.tipo || ''),
          natureza: String(obj.natureza || ''),
          valores: this.normalizarNumeros(obj.valores),
          total: Number(obj.total || 0),
          filhos: this.normalizarLinhas(obj.filhos)
        } as FluxoCaixaLinha;
      })
      .filter((x: FluxoCaixaLinha) => x.id > 0);
  }

  private montarLinhasExcel(
    nos: FluxoCaixaLinha[],
    naturezaRef: 'ENTRADA' | 'SAIDA',
    nivel = 0,
    basePercentual = 0
  ): LinhaExcel[] {
    const linhas: LinhaExcel[] = [];
    (nos || []).forEach((item) => {
      if (String(item?.natureza || '').toUpperCase() === naturezaRef) {
        const prefixo = '  '.repeat(Math.max(nivel, 0));
        const total = Number(item.total || 0);
        const pct = this.calcularPercentualLinha(item, basePercentual);
        linhas.push([
          `${prefixo}${item.codigo} ${item.nome}`.trim(),
          ...(item.valores || []).map((v) => Number(v || 0)),
          total,
          this.calcularMedia(total, item.valores),
          pct != null ? `${pct.toFixed(2).replace('.', ',')}%` : '-'
        ]);
      }
      if (item?.filhos?.length) {
        linhas.push(...this.montarLinhasExcel(item.filhos, naturezaRef, nivel + 1, Number(item.total || 0)));
      }
    });
    return linhas;
  }

  private marcarExpandido(linhas: FluxoCaixaLinha[]): void {
    (linhas || []).forEach((linha) => {
      this.expandido[linha.id] = true;
      if (linha.filhos?.length) this.marcarExpandido(linha.filhos);
    });
  }
}
